import { extractLlmSeries } from './helpers.ts';
import type { ShowDashboardInput } from './schema.ts';
import type { Dataset } from '#plugin-chat/lib/data-pipeline/types.ts';

/** Component types handled by the dashboard adapters. */
// FIXME: derive from SSOT
const KNOWN_COMPONENT_TYPES = new Set(['chart', 'table', 'kpi', 'markdown']);

/**
 * Collect every dataset key referenced by the dashboard spec — item-level
 * `inputDataset` for tables/kpis and per-series `inputDataset` for charts.
 */
export function collectReferencedSourceKeys(items: ShowDashboardInput['items']): string[] {
  return Array.from(
    new Set(
      items.flatMap((item) => [
        ...(typeof item.component.inputDataset === 'string' ? [item.component.inputDataset] : []),
        ...extractLlmSeries(item.component as Record<string, unknown>).map((s) => s.inputDataset),
      ]),
    ),
  );
}

/**
 * Validate a dashboard spec against the datasets it references.
 *
 * Returns a list of human-readable error messages, one per rendering
 * problem. An empty list means the spec is renderable.
 *
 * This is the same validation the render-side adapters perform, lifted
 * into a pure function so it can also run inside
 * `ShowDashboardTool.execute` and surface errors to the LLM as
 * `ToolExecutionError` instead of silently producing a `<BlockError>`
 * that only the user sees.
 */
export function validateDashboardSpec(
  items: ShowDashboardInput['items'],
  datasets: ReadonlyMap<string, Dataset | undefined>,
): string[] {
  return items.flatMap((item, index) => {
    const label = `items[${index}]`;
    const componentType = (item.component as { type?: unknown }).type;

    if (typeof componentType !== 'string') {
      console.info('Validate', { item }, item.component?.type);
      return [`${label}: component type is missing.`];
    }
    if (!KNOWN_COMPONENT_TYPES.has(componentType)) {
      return [`${label}: unknown component type "${componentType}".`];
    }

    // const component = item.component as Record<string, unknown>;

    // if (componentType === 'chart') {
    //   return validateChart(label, component, extractLlmSeries(component), item.inputDataset, datasets);
    // }
    if (item.component.type === 'table') {
      return validateItemSourceKey(label, componentType, item.component.inputDataset, datasets);
    }
    return [];
  });
}

// /**
//  * Build a Set of a dataset's column names. Returns an empty set when
//  * the dataset is missing — callers gate on `datasets.get(key)` first
//  * so the no-dataset case never reaches this path, but the fallback
//  * keeps the return type non-nullable and simplifies call sites.
//  */
// function datasetColumns(datasets: ReadonlyMap<string, Dataset | undefined>, key: string): ReadonlySet<string> {
//   return new Set((datasets.get(key)?.columns ?? []).map((c) => c.name));
// }
//
// /**
//  * Compose the canonical "column X not found in dataset Y" error. Shared
//  * by the series-column check and the xAxis-column check so both surface
//  * a consistent message shape and the list of available columns the LLM
//  * can choose from.
//  */
// function missingColumnMessage(
//   label: string,
//   role: string,
//   column: string,
//   inputDataset: string,
//   datasets: ReadonlyMap<string, Dataset | undefined>,
// ): string {
//   const dataset = datasets.get(inputDataset);
//   const available = (dataset?.columns ?? []).map((c) => `"${c.name}"`).join(', ');
//   return `${label} (chart): ${role} "${column}" not found in dataset "${inputDataset}". Available columns: ${available}.`;
// }
//
// function validateChart(
//   label: string,
//   component: Record<string, unknown>,
//   llmSeries: ReturnType<typeof extractLlmSeries>,
//   itemSourceKey: string | undefined,
//   datasets: ReadonlyMap<string, Dataset | undefined>,
// ): string[] {
//   if (llmSeries.length === 0) {
//     if (typeof itemSourceKey !== 'string') {
//       return [`${label} (chart): requires either item-level inputDataset or per-series inputDataset.`];
//     }
//     return datasets.has(itemSourceKey)
//       ? []
//       : [`${label} (chart): inputDataset "${itemSourceKey}" does not resolve to a staged dataset.`];
//   }
//
//   const inputDatasets = Array.from(new Set(llmSeries.map((s) => s.inputDataset)));
//
//   const unresolvedSourceErrors = inputDatasets
//     .filter((key) => !datasets.has(key))
//     .map((key) => `${label} (chart): series inputDataset "${key}" does not resolve to a staged dataset.`);
//
//   const duplicates = findDuplicateSeriesNames(llmSeries);
//   const duplicateErrors =
//     duplicates.length > 0 ? [`${label} (chart): ${formatDuplicateSeriesMessage(duplicates)}`] : [];
//
//   // xAxis column must be present in every referenced dataset when the
//   // chart explicitly declares one. Multi-source charts join rows across
//   // datasets on this column (see `mergeDatasetsByXaxis`); a dataset
//   // missing the column contributes no rows to the join and its series
//   // silently render as legend-only entries with no data points. We only
//   // validate when `component.xAxis` is an explicit string — leaving it
//   // unset falls back to chart-type defaults that some non-cartesian
//   // types (pie, donut, funnel, radar, ...) don't consume.
//   const xAxis = typeof component.xAxis === 'string' ? component.xAxis : undefined;
//
//   // Accept dotted-path column refs (e.g. `oil_daily_history.timestamp`) when
//   // the root column exists in the dataset. Recharts resolves dotted-path
//   // dataKeys natively at render time via lodash-style nested access, so the
//   // validator only needs to confirm the parent column is present.
//   // {@link dottedPathParent} is reused from the engine-side path resolver so
//   // the validator and the executor agree on what "root segment" means.
//   const columnAvailable = (columns: ReadonlySet<string>, ref: string): boolean =>
//     columns.has(ref) || columns.has(dottedPathParent(ref));
//
//   const seriesColumnErrors = llmSeries
//     .filter(
//       (series) =>
//         datasets.get(series.inputDataset) &&
//         !columnAvailable(datasetColumns(datasets, series.inputDataset), series.column),
//     )
//     .map((series) => missingColumnMessage(label, 'series column', series.column, series.inputDataset, datasets));
//
//   // No auto-flatten exemption: the chart adapter no longer reshapes datapoint
//   // arrays at render time. Pipelines whose output feeds a chart must end with
//   // an `expandTimeseries` action, which produces the canonical `timestamp`
//   // column directly. xAxis presence is now a strict structural check.
//
//   const xAxisErrors =
//     xAxis === undefined
//       ? []
//       : inputDatasets
//           .filter((key) => datasets.get(key) && !columnAvailable(datasetColumns(datasets, key), xAxis))
//           .map(
//             (key) =>
//               `${missingColumnMessage(label, "chart's xAxis column", xAxis, key, datasets)} Every dataset referenced by a chart series must include the xAxis column so rows from different sources can align; derive or project a matching column upstream with \`transformDataset\` (e.g. when overlaying monthly averages on a daily \`timestamp\` axis, derive a \`timestamp\` on the monthly dataset pointing at the first day of each month instead of keeping only a \`month\` string).`,
//           );
//
//   return [...unresolvedSourceErrors, ...duplicateErrors, ...seriesColumnErrors, ...xAxisErrors];
// }

function validateItemSourceKey(
  label: string,
  componentType: string,
  itemSourceKey: string | undefined,
  datasets: ReadonlyMap<string, Dataset | undefined>,
): string[] {
  if (typeof itemSourceKey !== 'string') {
    // Tables always need a inputDataset; KPIs may have an inline value instead,
    // so the render layer is responsible for the inline-value fallback —
    // here we only flag when it's clearly missing for tables.
    return componentType === 'table'
      ? [`${label} (table): property \`component.table.inputDataset\` must be set to the input dataset`]
      : [];
  }
  return datasets.has(itemSourceKey)
    ? []
    : [`${label} (${componentType}): inputDataset "${itemSourceKey}" does not resolve to a staged dataset.`];
}
